/**
 * Утилита для преобразования значений полей между форматом отображения и сохранения
 */
/* global BigInt */

export const MIN_NUMBER_INPUT_VALUE = -999999999999.99;
export const MAX_NUMBER_INPUT_VALUE = 999999999999.99;
export const MIN_INTEGER_INPUT_VALUE = -999999999;
export const MAX_INTEGER_INPUT_VALUE = 999999999;

/**
 * Парсит строку как число, заменяя запятые на точки и удаляя пробелы
 * @param {string|number} rawValue - сырое значение из инпута
 * @returns {number|null} - распарсенное число или null
 */
export const parseNumberInput = (rawValue) => {
  if (rawValue === null || rawValue === undefined || rawValue === "") {
    return null;
  }

  const sanitized = normalizeDecimalInput(rawValue);
  return parseFloat(sanitized);
};

export const isNumberInputInRange = (rawValue) => {
  if (rawValue === null || rawValue === undefined || rawValue === "") {
    return true;
  }

  const numericValue = parseNumberInput(rawValue);
  return (
    Number.isFinite(numericValue) &&
    numericValue >= MIN_NUMBER_INPUT_VALUE &&
    numericValue <= MAX_NUMBER_INPUT_VALUE
  );
};

export const NUMBER_INPUT_RANGE_MESSAGE = "Число вне допустимого диапазона";

export const isIntegerInputInRange = (rawValue) => {
  if (rawValue === null || rawValue === undefined || rawValue === "") {
    return true;
  }

  const numericValue = parseNumberInput(rawValue);
  return (
    Number.isInteger(numericValue) &&
    numericValue >= MIN_INTEGER_INPUT_VALUE &&
    numericValue <= MAX_INTEGER_INPUT_VALUE
  );
};

export const normalizeDecimalInput = (rawValue) =>
  rawValue === null || rawValue === undefined
    ? ""
    : String(rawValue).replace(/,/g, ".").replace(/\s/g, "");

/**
 * Добавляет пробелы между разрядами, не изменяя десятичный разделитель.
 * Подходит для formatter компонента InputNumber, включая частично введённые значения.
 */
export const formatNumberWithThousands = (rawValue) => {
  if (rawValue === null || rawValue === undefined || rawValue === "") {
    return "";
  }

  const normalizedValue = String(rawValue)
    .replace(/\s/g, "")
    .replace(/,/g, ".");
  const [integerPart, ...fractionParts] = normalizedValue.split(".");
  const groupedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, " ");

  return [groupedInteger, ...fractionParts].join(".");
};

const parseDecimalValue = (rawValue) => {
  const normalizedValue = normalizeDecimalInput(rawValue);
  const match = normalizedValue.match(/^([+-]?)(\d+)(?:\.(\d+))?$/);
  if (!match) return null;

  const [, sign, integerPart, fractionPart = ""] = match;
  const coefficient = BigInt(`${integerPart}${fractionPart}`);

  return {
    coefficient: sign === "-" ? -coefficient : coefficient,
    scale: fractionPart.length,
  };
};

export const sumDecimalValues = (values) => {
  const parsedValues = values.map(parseDecimalValue).filter(Boolean);
  const scale = Math.max(0, ...parsedValues.map((value) => value.scale));
  const sum = parsedValues.reduce(
    (total, value) =>
      total + value.coefficient * 10n ** BigInt(scale - value.scale),
    0n,
  );
  const isNegative = sum < 0n;
  const absoluteValue = isNegative ? -sum : sum;
  const digits = absoluteValue.toString().padStart(scale + 1, "0");
  const integerPart = scale ? digits.slice(0, -scale) : digits;
  const fractionPart = scale ? digits.slice(-scale).replace(/0+$/, "") : "";

  return `${isNegative ? "-" : ""}${integerPart}${
    fractionPart ? `.${fractionPart}` : ""
  }`;
};

export const formatDecimalValue = (rawValue, maximumFractionDigits = 2) => {
  const parsedValue = parseDecimalValue(rawValue);
  if (!parsedValue) return "";

  const targetScale = Math.min(parsedValue.scale, maximumFractionDigits);
  const scaleDifference = parsedValue.scale - targetScale;
  const divisor = 10n ** BigInt(scaleDifference);
  const absoluteCoefficient =
    parsedValue.coefficient < 0n
      ? -parsedValue.coefficient
      : parsedValue.coefficient;
  let roundedValue = absoluteCoefficient / divisor;

  if (scaleDifference && (absoluteCoefficient % divisor) * 2n >= divisor) {
    roundedValue += 1n;
  }

  const digits = roundedValue.toString().padStart(targetScale + 1, "0");
  const integerPart = targetScale ? digits.slice(0, -targetScale) : digits;
  const fractionPart = targetScale
    ? digits.slice(-targetScale).replace(/0+$/, "")
    : "";
  const sign = parsedValue.coefficient < 0n && roundedValue !== 0n ? "-" : "";

  return `${sign}${formatNumberWithThousands(integerPart)}${
    fractionPart ? `,${fractionPart}` : ""
  }`;
};

const parseCentsValue = (centsValue) => {
  if (typeof centsValue === "bigint") return centsValue;

  if (typeof centsValue === "number") {
    return Number.isFinite(centsValue) && Number.isInteger(centsValue)
      ? BigInt(centsValue)
      : null;
  }

  if (typeof centsValue === "string") {
    const normalizedValue = centsValue.replace(/\s/g, "");

    if (/^[+-]?\d+$/.test(normalizedValue)) {
      return BigInt(normalizedValue);
    }
  }

  return null;
};

export const isMoneyValueInRange = (centsValue) => {
  if (centsValue === null || centsValue === undefined || centsValue === "") {
    return true;
  }

  const parsedValue = parseCentsValue(centsValue);
  return (
    parsedValue !== null &&
    parsedValue >= -99999999999999n &&
    parsedValue <= 99999999999999n
  );
};

export const sumMoneyValues = (values) =>
  values.reduce((sum, value) => sum + (parseCentsValue(value) ?? 0n), 0n);

/**
 * Форматирует хранящееся в копейках значение для отображения в рублях.
 */
export const formatMoney = (centsValue) => {
  if (centsValue === null || centsValue === undefined || centsValue === "") {
    return null;
  }

  const parsedValue = parseCentsValue(centsValue);
  if (parsedValue === null) return null;

  const isNegative = parsedValue < 0n;
  const absoluteValue = isNegative ? -parsedValue : parsedValue;
  const rubles = absoluteValue / 100n;
  const cents = absoluteValue % 100n;
  const formattedRubles = formatNumberWithThousands(rubles.toString());
  const sign = isNegative ? "-" : "";

  return `${sign}${formattedRubles}.${cents.toString().padStart(2, "0")} руб.`;
};

/**
 * Форматирует значение, уже выраженное в рублях.
 */
export const formatRubles = (rublesValue) => {
  const numericValue = parseNumberInput(rublesValue);
  if (numericValue === null || !Number.isFinite(numericValue)) return null;

  return `${formatNumberWithThousands(numericValue.toFixed(2))} руб.`;
};

/**
 * Парсит значение денег (в копейках) в рубли для отображения
 * @param {number|string} centsValue - значение в копейках (например 12345 = 123.45 руб)
 * @returns {string|number|null} - значение в рублях для отображения
 */
export const parseCentsToDisplay = (centsValue) => {
  if (!centsValue && centsValue !== 0) return null;
  const parsedValue = parseCentsValue(centsValue);
  if (parsedValue === null) return null;

  const isNegative = parsedValue < 0n;
  const absoluteValue = isNegative ? -parsedValue : parsedValue;
  const rubles = absoluteValue / 100n;
  const cents = absoluteValue % 100n;

  return `${isNegative ? "-" : ""}${rubles}.${cents
    .toString()
    .padStart(2, "0")}`;
};

/**
 * Преобразует рубли в копейки для сохранения
 * @param {number|string} rublesValue - значение в рублях
 * @returns {string} - значение в копейках как строка
 */
export const parseDisplayToCents = (rublesValue) => {
  if (!rublesValue && rublesValue !== 0) return null;
  const parsedValue = parseDecimalValue(rublesValue);
  if (!parsedValue) return null;

  if (parsedValue.scale <= 2) {
    return (
      parsedValue.coefficient *
      10n ** BigInt(2 - parsedValue.scale)
    ).toString();
  }

  const divisor = 10n ** BigInt(parsedValue.scale - 2);
  const isNegative = parsedValue.coefficient < 0n;
  const absoluteValue = isNegative
    ? -parsedValue.coefficient
    : parsedValue.coefficient;
  let roundedValue = absoluteValue / divisor;

  if ((absoluteValue % divisor) * 2n >= divisor) {
    roundedValue += 1n;
  }

  return `${isNegative ? "-" : ""}${roundedValue}`;
};
